/**
 * Validator for DynamicConfiguration
 *
 * Validates dynamic configuration data loaded from CDN.
 */

import type { Availability, DynamicConfiguration, FeatureFlags } from '@/domain/configuration';
import { AVAILABILITY } from '@/domain/configuration';
import {
  among,
  boolean,
  field,
  isValid,
  number,
  object,
  optionalField,
  record,
  validate,
} from '@/lib/utils/validator/validator';

export function validDynamicConfiguration(value: unknown): DynamicConfiguration {
  return validate(value, dynamicConfigurationValidator);
}

function validAvailability(value: unknown): Availability {
  return among(Object.values(AVAILABILITY))(value);
}

// Supernova stores feature flags as booleans; anything else is corrupt config.
function validFeatureFlags(value: unknown): FeatureFlags {
  return record(boolean)(value);
}

function dynamicConfigurationValidator(input: unknown): DynamicConfiguration {
  return object(input, () => {
    const enabledValue = field('enabled', boolean, { default: true });
    const enabled = isValid(enabledValue) ? enabledValue : true;

    const availabilityValue = field('availability', validAvailability, {
      default: AVAILABILITY.BUSINESS_CLOSED,
    });
    const availability = isValid(availabilityValue) ? availabilityValue : AVAILABILITY.BUSINESS_CLOSED;

    const percentageRolloutValue = field('percentageRollout', number, {
      default: 0,
    });
    const percentageRollout = isValid(percentageRolloutValue) ? percentageRolloutValue : 0;

    return {
      enabled,
      availability,
      percentageRollout,
      featureFlags: optionalField('featureFlags', validFeatureFlags),
    };
  });
}
